import { DotsHorizontalIcon, Pencil1Icon, ReaderIcon } from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { CompetitionParticipantsDatatable } from '../components/datatables/competitionParticipantsDatatable';
import { CompetitionSportClassesDatatable } from '../components/datatables/competitionSportClassesDatatable';
import AddCompetitionSportClassDialog from '../components/dialogs/addCompetitionSportClassDialog';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from '../components/ui/button';
import {
    Dialog,
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from '../components/ui/skeleton';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';
// import { CompetitionParticipantsCollapsableDatatable } from '../components/datatables/competitionParticipantsCollapsableDatatable';
import { CompetitionCupsDatatable } from '../components/datatables/competitionCupsDatatable';
import CreateCupDialog from '../components/dialogs/createCupDialog';
import DeleteCompetitionDialog from '../components/dialogs/deleteCompetitionDialog';
import EmailCompetitionParticipantsDialog from '../components/dialogs/EmailCompetitionParticipantsDialog';
import ExternalLinkWrapper from '../components/externalLinkWrapper';
import { Badge } from '../components/ui/badge';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { toast } from '../components/ui/use-toast';
import { formatDateToYYYYMMDD } from '../lib/dateHelper';
import { cn } from '../lib/utils';

const CompetitionSingle = () => {
    const { competitionId } = useParams();
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();

    const contentRef = useRef(null);

    const [competition, setCompetition] = useState({});
    const [participants, setParticipants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [participantsIsLoading, setParticipantsIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCompetition = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/information/${competitionId}`);

            setCompetition(response.data);
            setError(null);
        } catch (error) {
            if (error.response?.status === 404) {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: 'Tävlingen du efterfrågade finns inte.',
                });
                navigate(`/upcoming`);
            }

            setError('Ett fel inträffade vid inhämtning av tävlingsdata. Var god försök senare.');
            setCompetition({});
        } finally {
            setIsLoading(false);
        }
    }, [competitionId, navigate]);

    const fetchParticipants = useCallback(async () => {
        try {
            setParticipantsIsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/${competitionId}/participants`);

            setParticipants(response.data);
            setError(null);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setError('Internt serverfel: Deltagare ej funna.');
            } else {
                setError('Ett fel inträffade vid inhämtning av deltagardata. Var god försök senare.');
            }
            setParticipants([]);
        } finally {
            setParticipantsIsLoading(false);
        }
    }, [competitionId]);

    useEffect(() => {
        fetchCompetition();
    }, [fetchCompetition]);

    useEffect(() => {
        if (!isLoading) {
            fetchParticipants();
        }
    }, [fetchParticipants, isLoading]);

    if (error) {
        return (
            <div className='flex w-full items-center justify-center'>
                <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                    <h1 className='font-medium text-2xl'>Error</h1>
                    <p className='text-red-500'>{error}</p>
                </div>
            </div>
        );
    }

    // Get the current date
    const currentDate = new Date();

    // Convert competition.registration_date to a Date object
    const lateRegistrationDate = new Date(competition?.late_registration_date);

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 items-start'>
                    <div>
                        {isLoading ? (
                            <div className='flex flex-col gap-3'>
                                <Skeleton className='h-8 w-full' />
                                <Skeleton className='h-4 w-40' />
                            </div>
                        ) : (
                            <>
                                <h1 className='font-medium text-2xl flex gap-3 items-center'>{competition?.name}
                                    {competition?.finished_at && <Badge variant='secondary'>Avslutad</Badge>}
                                </h1>
                                <Breadcrumb className='mt-2'>
                                    <BreadcrumbList>
                                        <BreadcrumbItem>
                                            {competition?.finished_at ? <BreadcrumbLink asChild><Link to="/results">Resultat</Link></BreadcrumbLink> : <BreadcrumbLink asChild ><Link to="/upcoming">Kommande tävlingar</Link></BreadcrumbLink>}
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbPage>{competition?.name}</BreadcrumbPage>
                                        </BreadcrumbItem>
                                    </BreadcrumbList>
                                </Breadcrumb>
                            </>
                        )}
                    </div>
                    <div className={`grid gap-3 pt-3 ${lateRegistrationDate > currentDate
                        ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
                        : 'grid-cols-1 sm:grid-cols-1 xl:grid-cols-2'
                        }`}>
                        {!isLoading && (lateRegistrationDate > currentDate) && (
                            <Button className={
                                !(auth.user?.superadmin ||
                                    (auth.user?.roleInClub === 'admin' &&
                                        auth.user?.memberOfClub === parseInt(competition?.club?.id)))
                                    ? 'sm:col-span-4'
                                    : 'sm:col-span-2'
                            }
                                onClick={() => navigate(`register`)}>
                                Anmäl till tävling
                            </Button>
                        )}
                        {(!isLoading && (auth.user?.superadmin || (auth.user?.roleInClub === 'admin' && auth.user?.memberOfClub === parseInt(competition?.club?.id)))) && (
                            <Dialog modal={true}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="secondary"
                                            className={cn(
                                                'sm:col-span-2', // Default styling for the button
                                                lateRegistrationDate > currentDate
                                                    ? 'col-start-1' // Align to the first column when "Anmäl" button is visible
                                                    : 'col-start-1' // Align to the second column when "Anmäl" button is not visible
                                            )}
                                        >
                                            <DotsHorizontalIcon className='mr-3' />Verktyg
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className='dropdown-content-width-full'>
                                        <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {(!competition?.finished_at &&
                                            <>
                                                <AddCompetitionSportClassDialog competition={competition} fetchCompetition={fetchCompetition} />
                                                <CreateCupDialog competitionId={competition?.id} reloadCompetitionSingle={fetchCompetition} />
                                                <DropdownMenuItem>
                                                    <Pencil1Icon className='mr-3'/>
                                                    <Link to={`/competition/${competitionId}/edit`}>Ändra tävlingsinformation</Link>
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        <EmailCompetitionParticipantsDialog competitionId={competitionId} />
                                        <DropdownMenuItem>
                                            <ReaderIcon className='mr-3'/>
                                            <Link to={`/system?competitionId=${competitionId}`}>Loggar för tävlingen</Link>
                                        </DropdownMenuItem>
                                        <DeleteCompetitionDialog competition={competition} />
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </Dialog>
                        )}
                    </div>
                </div>
                <Accordion type="single" collapsible defaultValue='item-1'>
                    <AccordionItem value="item-1">
                        <AccordionTrigger>Tävlingsinformation</AccordionTrigger>
                        <AccordionContent className='flex flex-col gap-3'>
                            {isLoading ? (
                                <div className='rounded-md bg-gray-100 p-3'>
                                    <Skeleton className='w-full h-8' />
                                </div>
                            ) : (
                                competition?.description ?
                                    <ExternalLinkWrapper>
                                        <div ref={contentRef} dangerouslySetInnerHTML={{ __html: competition?.description }}
                                            className='rounded-md bg-gray-100 p-3 [&_a]:text-blue-600 [&_a:hover]:underline [&_svg]:text-blue-600 [&_h1]:text-lg' />
                                    </ExternalLinkWrapper>
                                    : <div className='rounded-md bg-gray-100 p-3'>Ingen tävlingsbeskrivning finns.</div>
                            )}
                            <div className='rounded-md bg-gray-100 p-3'>
                                {isLoading ? (
                                    <div className='flex flex-col w-1/2 gap-2'>
                                        <Skeleton className='h-4 w-2/3' />
                                        <Skeleton className='h-4 w-5/6' />
                                        <Skeleton className='h-4 w-3/4' />
                                        <Skeleton className='h-4 w-2/3' />
                                        <Skeleton className='h-4 w-4/6' />
                                    </div>
                                ) : (
                                    <>
                                        <p><span className='font-medium'>Startdatum:</span> {formatDateToYYYYMMDD(competition?.start_date)}</p>
                                        <p><span className='font-medium'>Slutdatum:</span> {formatDateToYYYYMMDD(competition?.end_date)}</p>
                                        <p><span className='font-medium'>Sista anmälningsdatum:</span> {formatDateToYYYYMMDD(competition?.registration_date)}</p>
                                        <p><span className='font-medium'>Sista efteranmälningsdag:</span> {formatDateToYYYYMMDD(competition?.late_registration_date)}</p>
                                        <p><span className='font-medium'>Arrangeras av:</span> <Link className='text-blue-600 cursor-pointer hover:underline' to={`/clubs/${competition?.club?.id}`}>{competition?.club?.name}</Link></p>
                                    </>
                                )}
                            </div>
                            <div className='rounded-md bg-gray-100 p-3'>
                                {isLoading ? (
                                    <div className='flex flex-col w-1/2 gap-2'>
                                        <Skeleton className='h-4 w-2/3' />
                                        <Skeleton className='h-4 w-5/6' />
                                        <Skeleton className='h-4 w-3/4' />
                                        <Skeleton className='h-4 w-2/3' />
                                        <Skeleton className='h-4 w-4/6' />
                                        <Skeleton className='h-4 w-3/4' />
                                    </div>
                                ) : (
                                    <>
                                        <p><span className='font-medium'>Ordinarie start:</span> {competition?.entry_price} kr</p>
                                        <p><span className='font-medium'>Efteranmälan:</span> {competition?.late_entry_price} kr</p>
                                        <p><span className='font-medium'>Utom tävlan:</span> {competition?.ooc_entry_price} kr</p>
                                        <p><span className='font-medium'>Mini start:</span> {competition?.mini_entry_price} kr</p>
                                        <p><span className='font-medium'>Cupstart:</span> {competition?.cup_entry_price} kr</p>
                                        <p><span className='font-medium'>Klassprövning:</span> {competition?.class_test_price} kr</p>
                                    </>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                <h2 className='font-medium text-xl mt-5'>Klasser</h2>
                {competition?.classes && (
                    <CompetitionSportClassesDatatable sportClasses={competition?.classes} isLoading={isLoading} fetchCompetition={fetchCompetition} competition={competition} />
                )}

                {competition?.cups?.length > 0 && (
                    <>
                        <h2 className='font-medium text-xl mt-5'>Cuper</h2>
                        <CompetitionCupsDatatable competition={competition} isLoading={isLoading} />
                    </>
                )}

                {participants?.length > 0 && (
                    <>
                        <h2 className='font-medium text-xl mt-5'>Deltagare</h2>
                        <CompetitionParticipantsDatatable participants={participants} isLoading={participantsIsLoading} fetchCompetition={fetchCompetition} fetchParticipants={fetchParticipants} competition={competition} />
                    </>
                )}

            </div>
        </div>
    );
};

export default CompetitionSingle;
